import { WidgetCard } from './atoms/WidgetCard';
import { WidgetBody } from './atoms/WidgetBody';
import { WidgetWithMissingConfig } from './atoms/WidgetWithMissingConfig';
import { WidgetErrorBody } from './atoms/WidgetErrorBody';
import { ChartBody } from './atoms/ChartBody';
import { DashboardModes } from '../types';
import { EditModeChartWidgetHeaderBar } from './atoms/EditModeChartWidgetHeaderBar';
import { NoDataDoqy } from './atoms/NoDataDoqy';
import { ViewModeHeaderBar } from './atoms/ViewModeHeaderBar';
import { MetricsModeHeaderBar } from './atoms/MetricsModeHeaderBar';
import { ErrorBoundary } from '@ardoq/error-boundary';
import { logError } from '@ardoq/logging';
import { isEmpty } from 'lodash';
import { FlexBox } from '@ardoq/layout';
import { GlobalFiltersWidgetMask } from './atoms/GlobalFiltersWidgetMask';
import { WidgetProps } from './widgetPropTypes';
import { Maybe } from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

export const ChartWidget = (
  props: WidgetProps & {
    dashboardContainerRef: Maybe<HTMLDivElement>;
    i18nLocale: I18nLocale;
  }
) => {
  const { i18nLocale } = props;
  const errorBoundaryMessage = t(
    i18nLocale,
    'dashboard:chartWidget.errorBoundary',
    'There is a technical issue with this chart. Please refresh to try again. If the problem continues, contact customer support.'
  );
  const configurationInstructions =
    props.dashboardMode === DashboardModes.EDIT
      ? props.configurationInstructions
      : [];

  return (
    <WidgetCard
      widgetId={props._id}
      dashboardMode={props.dashboardMode}
      layout={props.layout}
      isSelected={props.isSelected}
      hasError={props.hasError}
      hasWarning={props.hasWarning}
    >
      {props.dashboardMode === DashboardModes.EDIT ? (
        <EditModeChartWidgetHeaderBar {...props.headerBarProps} />
      ) : props.dashboardMode === DashboardModes.METRICS ? (
        <MetricsModeHeaderBar {...props.headerBarProps} />
      ) : (
        <ViewModeHeaderBar {...props.headerBarProps} />
      )}
      <ErrorBoundary
        logError={logError}
        errorMessage={
          <WidgetErrorBody
            errors={[errorBoundaryMessage]}
            i18nLocale={i18nLocale}
          />
        }
      >
        <WidgetBody
          hasHeader={true}
          isLoading={
            props.dashboardMode === DashboardModes.EDIT && props.isLoading
          }
        >
          {props.isValidWidgetData ? (
            <FlexBox
              flexDirection="column"
              justify="space-between"
              height="full"
            >
              <ChartBody
                dashboardContainerRef={props.dashboardContainerRef}
                {...props.chartBodyProps}
                i18nLocale={i18nLocale}
              />
              {props.dashboardMode === DashboardModes.EDIT &&
              props.isInLinkDashboardFiltersMode &&
              props.globalFiltersWidgetMaskProps ? (
                <GlobalFiltersWidgetMask
                  {...props.globalFiltersWidgetMaskProps}
                />
              ) : null}
            </FlexBox>
          ) : props.hasError ? (
            <WidgetErrorBody
              errors={props.viewValidationErrors}
              i18nLocale={i18nLocale}
            />
          ) : !isEmpty(configurationInstructions) ? (
            <WidgetWithMissingConfig
              configurationInstructions={configurationInstructions}
            />
          ) : (
            <NoDataDoqy
              dashboardMode={props.dashboardMode}
              i18nLocale={i18nLocale}
            />
          )}
        </WidgetBody>
      </ErrorBoundary>
    </WidgetCard>
  );
};
